// Deterministic, DOM-free transformations applied to composition HTML before it loads into the iframe.

import type { HtmlFontFace } from './htmlFonts';

export const TEMPLATE_COLOR_STYLE_ID = 'l5-template-colors';

/** Id of the injected style carrying the `--l5-font-N` variables and their `@font-face` rules. */
export const HTML_FONT_STYLE_ID = 'l5-fonts';

/** Prefix of the injected font custom properties (e.g. `--l5-font-0`), indexed like the template `fonts`. */
export const TEMPLATE_FONT_VAR_PREFIX = '--l5-font-';

export const GSAP_SCRIPT_ID = 'l5-gsap';

/** The pinned gsap build injected into every composition: deterministic version, CSP-allowlisted CDN. */
export const GSAP_CDN_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/gsap.min.js';

/** Prefix of the injected palette custom properties (e.g. `--l5-color-0`), indexed like the template `colors`. */
export const TEMPLATE_COLOR_VAR_PREFIX = '--l5-color-';

/** The `var(...)` reference an author uses to read template color `index`. */
export function htmlTemplateColorVar(index: number): string {
    return `var(${TEMPLATE_COLOR_VAR_PREFIX}${index})`;
}

export function templateColorToHex(color: number): string {
    return '#' + ((color >>> 0) & 0xffffff).toString(16).padStart(6, '0');
}

export function buildTemplateColorCss(palette: string[]): string {
    const declarations = palette
        .map((color, index) => `${TEMPLATE_COLOR_VAR_PREFIX}${index}: ${color};`)
        .join(' ');
    return `:root { ${declarations} }`;
}

/** Insert or replace the template-color `<style>` block — idempotent (swaps rather than appends). */
export function injectTemplateColorVariables(html: string, palette: string[]): string {
    const styleTag = `<style id="${TEMPLATE_COLOR_STYLE_ID}">${buildTemplateColorCss(palette)}</style>`;

    const existingBlock = new RegExp(
        `<style id="${TEMPLATE_COLOR_STYLE_ID}">[\\s\\S]*?</style>`,
        'i',
    );
    const withoutExisting = html.replace(existingBlock, '');

    if (/<\/head>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<\/head>/i, `${styleTag}</head>`);
    }
    if (/<head[^>]*>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<head[^>]*>/i, (match) => `${match}${styleTag}`);
    }
    return `${styleTag}${withoutExisting}`;
}

/**
 * The `--l5-font-N` variable declarations plus a matching `@font-face` for each font. The variable
 * carries the family name so the composition references the font indirectly (`var(--l5-font-N)`)
 * and follows template-font changes — the same indirection the palette uses. Faces are data URIs
 * so both the iframe layout and snapDOM's SVG-image rasterization can use them (an SVG-as-image
 * can't fetch external font URLs).
 */
export function buildFontCss(fonts: HtmlFontFace[]): string {
    const declarations = fonts
        .map((font) => `${TEMPLATE_FONT_VAR_PREFIX}${font.index}: '${font.familyName}';`)
        .join(' ');
    // Different slots can resolve to the same font; emit each unique family's @font-face once so its
    // (large) bytes aren't embedded — and re-serialized on every capture — more than once.
    const seenFamilies = new Set<string>();
    const faces = fonts
        .filter((font) => {
            if (seenFamilies.has(font.familyName)) {
                return false;
            }
            seenFamilies.add(font.familyName);
            return true;
        })
        .map(
            (font) =>
                `@font-face { font-family: '${font.familyName}'; src: url(${font.dataUri}); }`,
        )
        .join(' ');
    return `:root { ${declarations} } ${faces}`;
}

/** Insert or replace the template-font `<style>` block (vars + faces) — idempotent. */
export function injectFonts(html: string, fonts: HtmlFontFace[]): string {
    const existingBlock = new RegExp(`<style id="${HTML_FONT_STYLE_ID}">[\\s\\S]*?</style>`, 'i');
    const withoutExisting = html.replace(existingBlock, '');
    if (!fonts.length) {
        return withoutExisting;
    }
    const styleTag = `<style id="${HTML_FONT_STYLE_ID}">${buildFontCss(fonts)}</style>`;

    if (/<\/head>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<\/head>/i, `${styleTag}</head>`);
    }
    if (/<head[^>]*>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<head[^>]*>/i, (match) => `${match}${styleTag}`);
    }
    return `${styleTag}${withoutExisting}`;
}

export function hasTimelineContract(html: string): boolean {
    return /__timelines/.test(html);
}

/** Id of the injected style that hides editable glyphs during natural text editing. */
export const NTE_EDITING_STYLE_ID = 'l5-nte-editing';

function escapeCssAttributeValue(value: string): string {
    return value.replace(/[\\"<>]/g, (char) => `\\${char.codePointAt(0)!.toString(16)} `);
}

/**
 * Hide the rasterized glyphs of the active editable region (layout preserved) so the editor's live
 * overlay is the only visible copy of that node. Injected only while editing.
 */
export function injectEditingHideStyle(html: string, activeRegionId: string): string {
    const selector = `[data-l5-editable="true"][id="${escapeCssAttributeValue(activeRegionId)}"]`;
    const styleTag = `<style id="${NTE_EDITING_STYLE_ID}">${selector} { color: transparent !important; }</style>`;
    if (/<\/head>/i.test(html)) {
        return html.replace(/<\/head>/i, `${styleTag}</head>`);
    }
    if (/<head[^>]*>/i.test(html)) {
        return html.replace(/<head[^>]*>/i, (match) => `${match}${styleTag}`);
    }
    return `${styleTag}${html}`;
}

/**
 * Inject the pinned gsap `<script>` at the start of `<head>` so it runs before the composition's
 * scripts; strips any author-included gsap tag so the pinned build wins.
 */
export function injectGsap(html: string): string {
    // Assembled from parts: a literal closing-script sequence in this source would end an inline
    // `<script>` block early when the luminary bundle is embedded inline.
    const closingScriptTag = '</' + 'script>';
    const scriptTag = `<script id="${GSAP_SCRIPT_ID}" src="${GSAP_CDN_URL}">${closingScriptTag}`;

    const existingGsapScript = /<script\b[^>]*\bsrc="[^"]*gsap[^"]*"[^>]*>\s*<\/script>/gi;
    const withoutExisting = html.replace(existingGsapScript, '');

    if (/<head[^>]*>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<head[^>]*>/i, (match) => `${match}${scriptTag}`);
    }
    if (/<\/head>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<\/head>/i, `${scriptTag}</head>`);
    }
    return `${scriptTag}${withoutExisting}`;
}

export interface ManageCompositionOptions {
    /** Id of the region being edited; when set, its raster glyphs are hidden. */
    editingRegionId?: string | null;
    /** Template fonts to inject as data-URI `@font-face` rules. */
    fonts?: HtmlFontFace[];
}

/** Apply all auto-management transformations, producing the HTML loaded into the iframe. */
export function manageComposition(
    html: string,
    palette: string[],
    options: ManageCompositionOptions = {},
): string {
    const withColors = injectTemplateColorVariables(injectGsap(html), palette);
    const withFonts = injectFonts(withColors, options.fonts ?? []);
    return options.editingRegionId
        ? injectEditingHideStyle(withFonts, options.editingRegionId)
        : withFonts;
}
